import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';

import type { LabelSaveFailure } from './label.state';

/*
 * In-memory representation of a LabelConfig change that has been submitted by
 * the user but not yet persisted
 */
export interface StagedLabelConfig {
  /**
   * Local-only id for diffing/removal
   */
  stagingId: string;

  /**
   * Original ID for label config if it already existed
   */
  originalConfigId?: string;

  /**
   * UUID of the label to assign the config to.
   * It may be a temporary frontend generated uuid if the label
   * is still being created.
   */
  labelUuid: string;

  /**
   * UUID of the method type to assign the config to.
   * It may be a temporary frontend generated uuid if the method type
   * is still being created.
   */
  methodTypeId: string;

  /**
   * Color of the config.
   */
  color: string;
}

/*
 * How this state keys its save failures internally. Private to this file: the
 * only thing outside needs is `saveFailures$`, which hands out the label uuid
 * directly, so no consumer ever decodes a key.
 */
const SAVE_EXCEPTION_KEY_SEPARATOR = '::';

interface LabelConfigStateModel {
  stagedConfigs: StagedLabelConfig[];
  saving: boolean;

  /*
   * Exceptions from the last bulk-save pass (set by the cascade, not by this
   * state directly), keyed by the failing row's post-consolidation
   * `labelUuid::methodTypeId` PAIR — the entity's own identity. A best-effort
   * tail can fail several method types of the SAME label at once, so keying by
   * label alone would collapse N failures into one entry. Raw `Error` so each
   * UI consumer can dispatch on the subclass and map to its own i18n namespace.
   */
  saveExceptions: Record<string, Error>;
}

@Injectable()
export class LabelConfigState extends ComponentStore<LabelConfigStateModel> {
  constructor() {
    super({ stagedConfigs: [], saving: false, saveExceptions: {} });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly stagedConfigs$ = this.select((state) => state.stagedConfigs);
  public readonly saving$ = this.select((state) => state.saving);

  /*
   * Private: the keys carry this state's own pair encoding, and nothing outside
   * should have to read it. `saveFailures$` below is the public surface.
   */
  private readonly saveExceptions$ = this.select((state) => state.saveExceptions);

  /**
   * Failures in the normalized shape consumers read. Rows are keyed internally by
   * the `(label, method type)` pair — the entity's own identity, so several method
   * types of the SAME label can fail distinguishably — and the label uuid is
   * handed out directly rather than left to be decoded from the key.
   */
  public readonly saveFailures$: Observable<LabelSaveFailure[]> = this.select(
    this.saveExceptions$,
    (saveExceptions): LabelSaveFailure[] =>
      Object.entries(saveExceptions).map(([key, error]) => ({
        rowId: key,
        labelUuid: key.split(SAVE_EXCEPTION_KEY_SEPARATOR)[0],
        error,
      }))
  );

  // ============================================================================
  // Updaters
  // ============================================================================

  /*
   * Upsert by `(labelUuid, methodTypeId)` — the entity itself is unique on
   * that pair, so pushing dedup into the state means the caller can use a
   * fresh stagingId per submission without tracking pair → id maps.
   */
  public readonly stageConfig = this.updater((state, config: StagedLabelConfig) => {
    const without = state.stagedConfigs.filter(
      (existing) => !(existing.labelUuid === config.labelUuid && existing.methodTypeId === config.methodTypeId)
    );
    return { ...state, stagedConfigs: [...without, config] };
  });

  public readonly removeConfig = this.updater((state, stagingId: string) => ({
    ...state,
    stagedConfigs: state.stagedConfigs.filter((entry) => entry.stagingId !== stagingId),
  }));

  /*
   * Eagerly rewrite staged configs' label uuids after label consolidation, so
   * the rows stay valid even when a later cascade phase fails and the retry
   * pass runs with an empty consolidation map (the persisted labels are no
   * longer staged then, so the map cannot be rebuilt).
   */
  public readonly remapLabelUuids = this.updater((state, mapping: Map<string, string>) => {
    if (mapping.size === 0) return state;
    return {
      ...state,
      stagedConfigs: state.stagedConfigs.map((config) => ({
        ...config,
        labelUuid: mapping.get(config.labelUuid) ?? config.labelUuid,
      })),
    };
  });

  /*
   * Same retry-safety for the method-type reference: once the row save returns
   * the real id, the placeholder is rewritten in place.
   */
  public readonly remapMethodTypeId = this.updater((state, payload: { from: string; to: string }) => ({
    ...state,
    stagedConfigs: state.stagedConfigs.map((config) =>
      config.methodTypeId === payload.from ? { ...config, methodTypeId: payload.to } : config
    ),
  }));

  public readonly clearStaged = this.updater((state) => ({ ...state, stagedConfigs: [] }));

  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));

  // Record one row's save failure keyed by its (post-consolidation) pair.
  // Does NOT touch `saving` — the cascade flips it once after the batch settles.
  public readonly recordSaveException = this.updater(
    (state, entry: { labelUuid: string; methodTypeId: string; error: Error }) => ({
      ...state,
      saveExceptions: {
        ...state.saveExceptions,
        [`${entry.labelUuid}${SAVE_EXCEPTION_KEY_SEPARATOR}${entry.methodTypeId}`]: entry.error,
      },
    })
  );

  // Clear all recorded save failures. No-op when already empty so a fresh `{}`
  // reference can't fire a spurious selector emit.
  public readonly clearSaveExceptions = this.updater((state) =>
    Object.keys(state.saveExceptions).length === 0 ? state : { ...state, saveExceptions: {} }
  );
}
